import { useContext, useEffect, useState } from "react";
import { format } from "date-fns";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { jwtDecode } from "jwt-decode";

import FEATURE_DEFINITIONS from "../../../Shared/Data/FeatureDefinitions.json";

import Icon from "../../../Shared/Components/IconElements/Icon";
import SearchInput from "../../../Shared/PurchasePro/Components/SearchInput/SearchInput";
import TheTable from "../../PortalComponents/TableElements/TheTable";
import TheTableHeader from "../../PortalComponents/TableElements/TheTableHeader";
import TheTablePagination from "../../PortalComponents/TableElements/TheTablePagination";
import TheTableWrapper from "../../PortalComponents/TableElements/TheTableWrapper";
import TheTableActionBox from "../../PortalComponents/TableElements/TheTableActionBox";

import Modal from "../../../Shared/Components/UI/TheModals/TheModal";
import TheButton from "../../../Shared/Components/UI/TheButton/TheButton";

import { AdminContext } from "../../../Context/Admin.Context";
import { AuthContext } from "../../../Context/Auth.Context";
import { LanguageContext } from "../../../Context/Language.Context";
import { RoutingContext } from "../../../Context/Routing.Context";
import { useFeatureDefinitionsHook } from "../../../Hooks/use-context-hooks";

const getLangKey = (language) => {
  return language === "FR" ? "fr" : "en";
};

const getProductType = (definition, language) => {
  const langKey = getLangKey(language);

  return (
    definition.productType?.[langKey] ||
    definition.productType?.en ||
    definition[language === "FR" ? "product-type-fr" : "product-type-en"] ||
    definition["product-type-en"] ||
    "Other"
  );
};

const getFeatureTitle = (definition, language) => {
  const langKey = getLangKey(language);

  return (
    definition.feature?.[langKey] ||
    definition.feature?.en ||
    definition[language === "FR" ? "feature-fr" : "feature-en"] ||
    definition["feature-en"] ||
    ""
  );
};

const getDefinitionText = (definition, language) => {
  const langKey = getLangKey(language);

  return (
    definition.definition?.[langKey] ||
    definition.definition?.en ||
    definition[language === "FR" ? "definition-fr" : "definition-en"] ||
    definition["definition-en"] ||
    ""
  );
};

const defaultModal = {
  show: false,
  title: "",
  message: "",
  errorList: [],
  actionText: "Close",
  wasSuccessful: false,
  onConfirm: null,
  onCancel: null,
  onClose: null,
  cancelText: "",
  confirmText: "",
};

const FeatureDefinitionsTablePage = () => {
  const { token } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);
  const { setIsRoutingState } = useContext(RoutingContext);
  const { setIsPortalActionState, setIsActiveSection } = useContext(AdminContext);

  const decodedToken = token ? jwtDecode(token) : null;

  const { definitions, isLoading, error, fetchDefinitions } =
    useFeatureDefinitionsHook();

  const [searchField, setSearchField] = useState("");
  const [filteredDefinitions, setFilteredDefinitions] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);

  const [selectedDefinitionForDeletion, setSelectedDefinitionForDeletion] =
    useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [modalInfo, setModalInfo] = useState(defaultModal);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const isUsingStaticData =
    !!error || !Array.isArray(definitions) || definitions.length === 0;

  const resolvedDefinitions = isUsingStaticData
    ? FEATURE_DEFINITIONS
    : definitions;

  const closeModal = () => {
    setModalInfo(defaultModal);
  };

  useEffect(() => {
    const q = searchField.trim().toLowerCase();

    if (!Array.isArray(resolvedDefinitions)) {
      setFilteredDefinitions([]);
      return;
    }

    if (q.length < 2) {
      setFilteredDefinitions(resolvedDefinitions);
      return;
    }

    const nextDefinitions = resolvedDefinitions.filter((definition) => {
      const productType = getProductType(definition, language).toLowerCase();
      const featureTitle = getFeatureTitle(definition, language).toLowerCase();
      const definitionText = getDefinitionText(definition, language).toLowerCase();

      return (
        productType.includes(q) ||
        featureTitle.includes(q) ||
        definitionText.includes(q)
      );
    });

    setFilteredDefinitions(nextDefinitions);
  }, [resolvedDefinitions, searchField, language]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchField, language]);

  const onSearchChange = (event) => {
    const searchFieldString = event.target.value.toLowerCase();

    setSearchField(searchFieldString);
    setIsSearchActive(searchFieldString.length > 0);
  };

  const handleDeleteDefinition = async (definitionId) => {
    setShowConfirmationModal(false);

    if (!definitionId) return;

    setIsRoutingState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));

    try {
      const response = await fetch(
        // `http://localhost:3000/delete-feature-definition/${definitionId}`,
        `${process.env.REACT_APP_BACKEND_URL}delete-feature-definition/${definitionId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            adminId: decodedToken?.userId,
          }),
        }
      );

      if (response.status === 200) {
        fetchDefinitions();

        setModalInfo({
          show: true,
          title: "Definition deleted",
          message: "The feature definition was deleted successfully.",
          errorList: [],
          confirmText: "Close",
          wasSuccessful: true,
          onConfirm: closeModal,
          onClose: closeModal,
        });
      } else {
        throw new Error("The feature definition could not be deleted.");
      }
    } catch (err) {
      setModalInfo({
        show: true,
        title: "Something went wrong",
        message: err.message,
        errorList: [],
        confirmText: "Try again",
        onConfirm: closeModal,
        onClose: closeModal,
      });
    } finally {
      setIsRoutingState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));

      setSelectedDefinitionForDeletion(null);
    }
  };

  // const columns = [
  //   {
  //     key: "productType",
  //     title: "Product Type",
  //     render: (row) => getProductType(row, language),
  //   },
  //   {
  //     key: "feature",
  //     title: "Feature",
  //     render: (row) => getFeatureTitle(row, language),
  //   },
  //   {
  //     key: "definition",
  //     title: "Definition",
  //     render: (row) => {
  //       const text = getDefinitionText(row, language);

  //       return text.length > 160 ? `${text.slice(0, 160)}...` : text;
  //     },
  //   },
  //   {
  //     key: "updatedAt",
  //     title: "Last Updated",
  //     render: (row) => {
  //       if (!row.updatedAt) return "Static";

  //       return format(new Date(row.updatedAt), "dd/MM/yyyy");
  //     },
  //   },
  //   {
  //     key: "actions",
  //     title: "Actions",
  //     render: (row) => (
  //       <TheTableActionBox>
  //         {!isUsingStaticData && (
  //           <>
  //             <span
  //               onClick={() => {
  //                 setIsPortalActionState((prevState) => ({
  //                   ...prevState,
  //                   editingFeatureDefinition: row._id,
  //                 }));

  //                 setIsActiveSection(14);
  //               }}
  //             >
  //               <Icon name="edit" />
  //             </span>

  //             <span
  //               onClick={() => {
  //                 setSelectedDefinitionForDeletion(row._id);
  //                 setShowConfirmationModal(true);
  //               }}
  //             >
  //               <Icon name="delete" />
  //             </span>
  //           </>
  //         )}
  //       </TheTableActionBox>
  //     ),
  //   },
  // ];
const columns = [
  {
    key: "productType",
    title: "Product Type",
    width: "16%",
    align: "left",
    render: (row) => getProductType(row, language),
  },
  {
    key: "feature",
    title: "Feature",
    width: "20%",
    align: "left",
    render: (row) => getFeatureTitle(row, language),
  },
  {
    key: "definition",
    title: "Definition",
    width: "42%",
    align: "left",
    wrap: true,
    render: (row) => getDefinitionText(row, language),
  },
  {
    key: "updatedAt",
    title: "Last Updated",
    width: "12%",
    align: "center",
    render: (row) => {
      if (!row.updatedAt) return "Static";
      return format(new Date(row.updatedAt), "dd/MM/yyyy");
    },
  },
  {
    key: "actions",
    title: "Actions",
    width: "10%",
    align: "center",
    render: (row) => (
      <TheTableActionBox>
          {!isUsingStaticData && (
            <>
              <span
                onClick={() => {
                  setIsPortalActionState((prevState) => ({
                    ...prevState,
                    editingFeatureDefinition: row._id,
                  }));

                  setIsActiveSection(14);
                }}
              >
                <Icon name="edit" />
              </span>

              <span
                onClick={() => {
                  setSelectedDefinitionForDeletion(row._id);
                  setShowConfirmationModal(true);
                }}
              >
                <Icon name="delete" />
              </span>
            </>
          )}
      </TheTableActionBox>
    ),
  },
];
  return (
    <>
      <TheTableWrapper>
        <TheTableHeader
          title="Feature Definitions"
          subtitle="Admin Portal"
          headline="View feature definitions below or search by product type, feature, or definition."
        />

        {isLoading && <p>Loading feature definitions...</p>}

        {isUsingStaticData && (
          <p style={{ marginBottom: "1rem" }}>
            Live feature definition data is currently unavailable. Static
            feature definitions are being shown.
          </p>
        )}

        <div style={{ marginBottom: "2rem" }}>
          <SearchInput
            id="search"
            icon={isSearchActive ? undefined : faSearch}
            onChange={onSearchChange}
          />
        </div>

        <TheTable
          columns={columns}
          data={filteredDefinitions.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
          )}
        />

        <TheTablePagination
          itemsPerPage={itemsPerPage}
          tableData={filteredDefinitions}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
        />
      </TheTableWrapper>

      <Modal
        icon
        iconName="close"
        styleName="lightIconButton"
        iconOnClick={modalInfo.onClose}
        show={modalInfo.show}
        title={modalInfo.title}
        message={modalInfo.message}
        errorList={modalInfo.errorList}
        closeOnBackdropClick={false}
        actionText={modalInfo.actionText}
        actionBox={
          <>
            {modalInfo.onConfirm && (
              <span onClick={modalInfo.onConfirm}>
                <TheButton styleName="red" animated="true">
                  {modalInfo.confirmText}
                </TheButton>
              </span>
            )}
          </>
        }
      />

      <Modal
        show={showConfirmationModal}
        title="You sure about this?"
        message="This action will permanently delete this feature definition. Are you sure you want to proceed?"
        onConfirm={() => handleDeleteDefinition(selectedDefinitionForDeletion)}
        onCancel={() => {
          setShowConfirmationModal(false);
          setSelectedDefinitionForDeletion(null);
        }}
      />
    </>
  );
};

export default FeatureDefinitionsTablePage;